/**
 * @fileoverview Spinner component for loading states.
 * @summary Reusable loading spinner component using FontAwesome.
 *
 * @package KaraoNow
 * @module apps/front/src/components/Spinner
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Spinner component props.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface SpinnerProps {
  /**
   * @summary Size of the spinner.
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
  /**
   * @summary Color variant of the spinner.
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'white'
  /**
   * @summary Additional CSS classes.
   */
  className?: string
}

/**
 * @summary Spinner component for loading states.
 * @param {SpinnerProps} props - Component props.
 * @returns {React.ReactElement} Spinner component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function Spinner({ size = 'md', variant = 'primary', className = '' }: SpinnerProps) {
  const sizeClasses = {
    sm: 'w-4! h-4!',
    md: 'w-5! h-5!',
    lg: 'w-6! h-6!',
    xl: 'w-12! h-12!',
    xxl: 'w-20! h-20!',
  }

  const variantClasses = {
    primary: 'spinner-primary',
    secondary: 'spinner-secondary',
    white: 'spinner-white',
  }

  return (
    <FontAwesomeIcon
      icon={faSpinner}
      className={`${sizeClasses[size]} ${variantClasses[variant]} animate-spin ${className}`}
      role="status"
      aria-label="Loading"
    />
  )
}
